/**
 * Finch Entity Card Registry
 *
 * Provides the appropriate card component for Finch entities
 * based on the canonical section type.
 */

import React from 'react';

import type { FinchEntity } from '@/services/api/entitySyncService';

import { isCoverageEntity, isMedicalProviderEntity } from '../../utils/typeGuards';
import { CoverageCard } from './CoverageCard';
import { MedicalProviderCard } from './MedicalProviderCard';

// Canonical section types
export type CanonicalSectionType = 'medical_providers' | 'coverage';

// Union of all possible Finch entity types

interface FinchEntityCardProps {
  entity: FinchEntity;
  canonicalSectionType: CanonicalSectionType;
  matterId?: string;
  className?: string;
  compact?: boolean;
}

/**
 * FinchEntityCard - Dynamic card component that renders the appropriate
 * card based on canonical section type.
 *
 * Add new section types here as they're supported.
 */
export const FinchEntityCard: React.FC<FinchEntityCardProps> = ({
  entity,
  canonicalSectionType,
  matterId,
  className,
  compact = false,
}) => {
  switch (canonicalSectionType) {
    case 'medical_providers':
      if (!isMedicalProviderEntity(entity)) {
        return <div>Not a medical provider</div>;
      }
      return (
        <MedicalProviderCard
          provider={entity}
          matterId={matterId}
          className={className}
          compact={compact}
        />
      );

    case 'coverage':
      if (!isCoverageEntity(entity)) {
        return <div>Not a coverage entity</div>;
      }
      return (
        <CoverageCard
          coverage={entity}
          matterId={matterId}
          className={className}
          compact={compact}
        />
      );

    default: {
      // Exhaustive check - TypeScript will error if we miss a case
      const _exhaustiveCheck: never = canonicalSectionType;
      return _exhaustiveCheck;
    }
  }
};

// Re-export individual cards for direct use if needed
export { CoverageCard } from './CoverageCard';
export { MedicalProviderCard } from './MedicalProviderCard';
